import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import type { IBlotterQuoteItem } from 'models/IBlotterItem';
import type { IBlotterColumnDefinition, PlatformFilter } from 'state/blotters/common/blotter.model';

import type { IQuoteBlotterFilters } from './quoteBlotter.model';

type QuoteBlotterFilterAction =
  | QuoteBlotterFilterChanged
  | QuoteBlotterAdvancedFiltersToggled
  | QuoteBlotterFiltersLoaded
  | QuoteBlotterFiltersLoadingFailed
  | QuoteBlotterFiltersPersistenceFailed;

type QuoteBlotterPreferenceAction =
  | QuoteBlotterPreferenceStateGet
  | QuoteBlotterPreferenceStateLoaded
  | QuoteBlotterPreferenceStateLoadingFailed
  | QuoteBlotterPreferenceStatePersisted
  | QuoteBlotterPreferenceStatePersistenceFailed;

export type QuoteBlotterAction =
  | QuoteBlotterFilterAction
  | QuoteBlotterPreferenceAction
  | QuoteBlotterColumnDefinitionReceived
  | QuoteBlotterColumnStateChanged
  | QuoteBlotterFetchingFailed
  | QuoteBlotterFilterByQuoteIds
  | QuoteBlotterItemsPerPageChanged
  | QuoteBlotterItemsReloaded
  | QuoteBlotterPageChanged
  | QuoteBlotterManualRefreshRequested
  | QuoteBlotterSortChanged
  | QuoteBlotterPlatforms
  | QuoteBlotterSetLoading;

//////////////////////////////////////////////////
export interface QuoteBlotterFiltersLoaded {
  type: 'QUOTE_BLOTTER_FILTERS_LOADED';
  filters: IQuoteBlotterFilters;
}
export function quoteBlotterFiltersLoaded(filters: IQuoteBlotterFilters): QuoteBlotterFiltersLoaded {
  return {
    type: 'QUOTE_BLOTTER_FILTERS_LOADED',
    filters,
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterFiltersLoadingFailed {
  type: 'QUOTE_BLOTTER_FILTERS_LOADING_FAILED';
}
export function quoteBlotterFiltersLoadingFailed(): QuoteBlotterFiltersLoadingFailed {
  return {
    type: 'QUOTE_BLOTTER_FILTERS_LOADING_FAILED',
  };
}

//////////////////////////////////////////////////
type Filter = keyof IQuoteBlotterFilters;

interface IQuoteBlotterFilterChanged<F extends Filter> {
  type: 'QUOTE_BLOTTER_FILTER_CHANGED';
  filter: F;
  value: IQuoteBlotterFilters[F];
}
export type QuoteBlotterFilterChanged = IQuoteBlotterFilterChanged<Filter>;

export function quoteBlotterFilterChanged<F extends Filter>(
  filter: F,
  value: IQuoteBlotterFilters[F],
): QuoteBlotterFilterChanged {
  return {
    type: 'QUOTE_BLOTTER_FILTER_CHANGED',
    filter,
    value,
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterAdvancedFiltersToggled {
  type: 'QUOTE_BLOTTER_ADVANCED_FILTERS_TOGGLED';
}
export function quoteBlotterAdvancedFiltersToggled(): QuoteBlotterAdvancedFiltersToggled {
  return {
    type: 'QUOTE_BLOTTER_ADVANCED_FILTERS_TOGGLED',
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterFiltersPersistenceFailed {
  type: 'QUOTE_BLOTTER_FILTERS_PERSISTENCE_FAILED';
}
export function quoteBlotterFiltersPersistenceFailed(): QuoteBlotterFiltersPersistenceFailed {
  return {
    type: 'QUOTE_BLOTTER_FILTERS_PERSISTENCE_FAILED',
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterColumnDefinitionReceived {
  type: 'QUOTE_BLOTTER_COLUMN_DEFINITION_RECEIVED';
  columnDefinition: IBlotterColumnDefinition<IBlotterQuoteItem>[];
}
export function quoteBlotterColumnDefinitionReceived(
  columnDefinition: IBlotterColumnDefinition<IBlotterQuoteItem>[],
): QuoteBlotterColumnDefinitionReceived {
  return {
    type: 'QUOTE_BLOTTER_COLUMN_DEFINITION_RECEIVED',
    columnDefinition,
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterPageChanged {
  type: 'QUOTE_BLOTTER_PAGE_CHANGED';
  page: number;
}
export function quoteBlotterPageChanged(page: number): QuoteBlotterPageChanged {
  return {
    type: 'QUOTE_BLOTTER_PAGE_CHANGED',
    page,
  };
}

//////////////////////////////////////////////////
export interface QuoteBlotterItemsPerPageChanged {
  type: 'QUOTE_BLOTTER_ITEMS_PER_PAGE_CHANGED';
  itemsPerPage: number;
}
export function quoteBlotterItemsPerPageChanged(itemsPerPage: number): QuoteBlotterItemsPerPageChanged {
  return {
    type: 'QUOTE_BLOTTER_ITEMS_PER_PAGE_CHANGED',
    itemsPerPage,
  };
}

//////////////////////////////////////////////////
export function quoteBlotterSetLoading(isLoading: boolean) {
  return { type: 'QUOTE_BLOTTER_SET_LOADING' as const, isLoading };
}

export type QuoteBlotterSetLoading = ReturnType<typeof quoteBlotterSetLoading>;

//////////////////////////////////////////////////
export interface QuoteBlotterItemsReloaded {
  type: 'QUOTE_BLOTTER_ITEMS_RELOADED';
}
export function quoteBlotterItemsReloaded(): QuoteBlotterItemsReloaded {
  return { type: 'QUOTE_BLOTTER_ITEMS_RELOADED' };
}

//////////////////////////////////////////////////
export interface QuoteBlotterColumnStateChanged {
  type: 'QUOTE_BLOTTER_COLUMN_STATE_CHANGED';
  columnState: readonly any[];
}
export function quoteBlotterColumnStateChanged(columnState: readonly any[]): QuoteBlotterColumnStateChanged {
  return { type: 'QUOTE_BLOTTER_COLUMN_STATE_CHANGED', columnState };
}

//////////////////////////////////////////////////
export function quoteBlotterSortChanged(sort: string | null, sortDir: 'asc' | 'desc' | null) {
  return { type: 'QUOTE_BLOTTER_SORT_CHANGED' as const, sort, sortDir };
}
export type QuoteBlotterSortChanged = ReturnType<typeof quoteBlotterSortChanged>;

////////////////////////////////////////////////
export interface QuoteBlotterFetchingFailed {
  type: 'QUOTE_BLOTTER_FETCHING_FAILED';
}
export function quoteBlotterFetchingFailed(): QuoteBlotterFetchingFailed {
  return { type: 'QUOTE_BLOTTER_FETCHING_FAILED' };
}

//////////////////////////////////////////////////
export interface QuoteBlotterPreferenceStatePersisted {
  type: 'QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function quoteBlotterPreferenceStatePersisted(
  blotterPreferenceState: IBlotterPreferenceState,
): QuoteBlotterPreferenceStatePersisted {
  return { type: 'QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTED', blotterPreferenceState };
}

//////////////////////////////////////////////////
export interface QuoteBlotterPreferenceStateLoaded {
  type: 'QUOTE_BLOTTER_PREFERENCE_STATE_LOADED';
  blotterPreferenceState: IBlotterPreferenceState;
}
export function quoteBlotterPreferenceStateLoaded(
  blotterPreferenceState: IBlotterPreferenceState,
): QuoteBlotterPreferenceStateLoaded {
  return { type: 'QUOTE_BLOTTER_PREFERENCE_STATE_LOADED', blotterPreferenceState };
}
//////////////////////////////////////////////////
export interface QuoteBlotterPreferenceStateGet {
  type: 'QUOTE_BLOTTER_PREFERENCE_STATE_GET';
}
export function quoteBlotterPreferenceStateGet(): QuoteBlotterPreferenceStateGet {
  return { type: 'QUOTE_BLOTTER_PREFERENCE_STATE_GET' };
}

///////////////////////////////////////////////

export function quoteBlotterPreferenceStateLoadingFailed(defaultPref: IBlotterPreferenceState) {
  return { type: 'QUOTE_BLOTTER_PREFERENCE_STATE_LOADING_FAILED' as const, defaultPref };
}
export type QuoteBlotterPreferenceStateLoadingFailed = ReturnType<typeof quoteBlotterPreferenceStateLoadingFailed>;
////////////////////////////////////////////////
export interface QuoteBlotterPreferenceStatePersistenceFailed {
  type: 'QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTENCE_FAILED';
}
export function quoteBlotterPreferenceStatePersistenceFailed(): QuoteBlotterPreferenceStatePersistenceFailed {
  return { type: 'QUOTE_BLOTTER_PREFERENCE_STATE_PERSISTENCE_FAILED' };
}

////////////////////////////////////////////////
export interface QuoteBlotterManualRefreshRequested {
  type: 'QUOTE_BLOTTER_MANUAL_REFRESH_REQUESTED';
}
export function quoteBlotterManualRefreshRequested(): QuoteBlotterManualRefreshRequested {
  return { type: 'QUOTE_BLOTTER_MANUAL_REFRESH_REQUESTED' };
}

////////////////////////////////////////////////

export function quoteBlotterFilterByQuoteIds(quoteIds: string[]) {
  return { type: 'QUOTE_BLOTTER_FILTER_BY_QUOTE_IDS' as const, quoteIds };
}
export type QuoteBlotterFilterByQuoteIds = ReturnType<typeof quoteBlotterFilterByQuoteIds>;

////////////////////////////////////////////////

export function quoteBlotterPlatformsFetching() {
  return { type: 'QUOTE_BLOTTER_PLATFORMS_FETCHING' as const };
}

type QuoteBlotterPlatformsFetching = ReturnType<typeof quoteBlotterPlatformsFetching>;

export function quoteBlotterPlatformsFetched(data: ReadonlyArray<PlatformFilter>) {
  return { type: 'QUOTE_BLOTTER_PLATFORMS_FETCHED' as const, data };
}

type QuoteBlotterPlatformsFetched = ReturnType<typeof quoteBlotterPlatformsFetched>;
export function quoteBlotterPlatformsFetchedFailed(error: Error) {
  return { type: 'QUOTE_BLOTTER_PLATFORMS_FETCH_FAILED' as const, error };
}
type QuoteBlotterPlatformsFetchFailed = ReturnType<typeof quoteBlotterPlatformsFetchedFailed>;

type QuoteBlotterPlatforms =
  | QuoteBlotterPlatformsFetching
  | QuoteBlotterPlatformsFetched
  | QuoteBlotterPlatformsFetchFailed;

////////////////////////////////////////////////
